/**
 * FestivalDetailModal · vista rápida de un festival al hacer clic en el
 * calendario. Muestra las ediciones, deadlines, links y las notas del
 * proyecto activo si las hay. Ofrece atajos para "Notas" o "Editar".
 *
 * Props:
 *   open        bool
 *   festival    objeto festival (con project_status opcional)
 *   isAdmin     bool
 *   onClose     () => void
 *   onOpenEdit  (festival) => void
 *   onOpenNotes (festival) => void
 */

const TYPE_LABEL = {
  festival: "Festival", market: "Mercado", pitch: "Pitch",
  forum: "Foro", residency: "Residencia", lab: "Lab",
};
const STATUS_LABEL = {
  interested: "Me interesa",
  submitted: "Enviado",
  accepted: "Aceptado",
  rejected: "Rechazado",
  discarded: "Descartado",
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
}

export default function FestivalDetailModal({ open, festival, isAdmin, projects = [], onClose, onOpenEdit, onOpenNotes }) {
  if (!open || !festival) return null;
  const linked = festival.project_statuses || [];
  const eds = festival.editions || [];
  const titleOf = (pid) => projects?.find(p => p.id === pid)?.title || pid;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" data-testid="festival-detail-modal">
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-stone-200 bg-gradient-to-br from-[#941620] to-[#7a1119] px-6 py-4 text-white">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/70">
              {TYPE_LABEL[festival.type] || festival.type}
            </p>
            <h2 className="mt-0.5 font-serif text-2xl leading-tight">{festival.name}</h2>
            <p className="mt-0.5 text-[11px] text-white/80">
              {festival.country}{festival.city ? ` · ${festival.city}` : ""}
            </p>
          </div>
          <button onClick={onClose}
                  data-testid="festival-detail-close"
                  className="rounded-full bg-white/20 px-2.5 py-0.5 text-lg text-white hover:bg-white/30">
            ×
          </button>
        </div>

        <div className="max-h-[65vh] overflow-y-auto px-6 py-5">
          {festival.description && (
            <p className="mb-4 text-sm leading-relaxed text-stone-700">{festival.description}</p>
          )}

          {/* Estado general Grabarte */}
          {(festival.studio_interested || festival.studio_attending || festival.studio_discarded || festival.studio_notes) && (
            <div className="mb-4">
              <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-stone-500">Estado general · Grabarte</h3>
              <div className="flex flex-wrap items-center gap-2">
                {festival.studio_interested && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-blue-800 ring-1 ring-blue-300">⭐ Me interesa</span>}
                {festival.studio_attending && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-emerald-800 ring-1 ring-emerald-300">🎟️ Vamos</span>}
                {festival.studio_discarded && <span className="rounded-full bg-stone-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-stone-700 ring-1 ring-stone-300">✖ Descartado</span>}
              </div>
              {festival.studio_notes && (
                <p className="mt-2 whitespace-pre-wrap rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-[12px] italic text-stone-700">{festival.studio_notes}</p>
              )}
            </div>
          )}

          {/* Proyectos ligados a este festival */}
          {linked.length > 0 && (
            <div className="mb-4">
              <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-stone-500">Nuestros proyectos</h3>
              <div className="space-y-2">
                {linked.map((ps) => (
                  <div key={ps.project_id} className="rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-[12px]">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-serif text-sm text-stone-900">{titleOf(ps.project_id)}</span>
                      {ps.status && (
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-blue-800 ring-1 ring-blue-300">
                          {STATUS_LABEL[ps.status] || ps.status}
                        </span>
                      )}
                      {ps.submitted && <span title="Inscritos">✉️</span>}
                      {ps.attending && <span title="Asistimos">🎟️</span>}
                    </div>
                    {ps.notes && (
                      <p className="mt-1 whitespace-pre-wrap text-[11px] italic text-stone-600">{ps.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ediciones */}
          <div className="mb-4">
            <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-stone-500">Ediciones</h3>
            <div className="space-y-2">
              {eds.length === 0 && <p className="text-xs text-stone-400">Sin ediciones cargadas.</p>}
              {eds.map((ed, i) => (
                <div key={i} className="rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-[12px]">
                  <div className="mb-1 flex items-center gap-2">
                    <span className="font-serif text-sm text-stone-900">{ed.edition_name || `Edición ${ed.year || ""}`}</span>
                    {!ed.is_confirmed && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-amber-700">Estimado</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-stone-700">
                    <div>
                      <span className="text-[10px] uppercase tracking-widest text-stone-500">Deadline</span>
                      <div>{fmt(ed.submission_deadline)}</div>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase tracking-widest text-stone-500">Evento</span>
                      <div>{fmt(ed.event_start)}{ed.event_end && ed.event_end !== ed.event_start ? ` → ${fmt(ed.event_end)}` : ""}</div>
                    </div>
                  </div>
                  {ed.notes && <p className="mt-1 text-[11px] italic text-stone-500">{ed.notes}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* Notas antiguas del "project_status" ya no aplican — usar el modal
              "Gestionar proyectos" para editar notas por proyecto. */}

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-3 text-[12px] text-stone-700">
            {festival.website && (
              <div className="col-span-2">
                <span className="text-[10px] uppercase tracking-widest text-stone-500">Web oficial</span>
                <div><a href={festival.website} target="_blank" rel="noopener noreferrer"
                        className="text-gb-red underline-offset-2 hover:underline">{festival.website}</a></div>
              </div>
            )}
            {festival.submission_fee_note && (
              <div className="col-span-2">
                <span className="text-[10px] uppercase tracking-widest text-stone-500">Coste de inscripción</span>
                <div>{festival.submission_fee_note}</div>
              </div>
            )}
            {(festival.tags || []).length > 0 && (
              <div className="col-span-2">
                <span className="text-[10px] uppercase tracking-widest text-stone-500">Tags</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {festival.tags.map((t, i) => (
                    <span key={i} className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] text-stone-600">{t}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stone-200 bg-stone-50 px-6 py-3">
          <button onClick={onClose}
                  data-testid="festival-detail-close-btn"
                  className="rounded-full border border-stone-300 bg-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-stone-700 hover:border-stone-500">
            Cerrar
          </button>
          {onOpenNotes && (
            <button onClick={() => onOpenNotes(festival)}
                    data-testid="festival-detail-notes"
                    className="rounded-full border border-[#941620] bg-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-[#941620] hover:bg-[#941620]/5">
              🎬 Gestionar proyectos
            </button>
          )}
          {isAdmin && onOpenEdit && (
            <button onClick={() => onOpenEdit(festival)}
                    data-testid="festival-detail-edit"
                    className="rounded-full bg-[#941620] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#7a1119]">
              ✎ Editar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
